import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller {
  static targets = ['input', 'results', 'spinner']

  connect() {
    this.selectedIndex = -1
    this.searchTimeout = null
    this.currentResults = []

    // Add keyboard shortcut listener
    document.addEventListener('keydown', this.handleGlobalKeydown)
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleGlobalKeydown)
  }

  handleGlobalKeydown = (event) => {
    // Cmd/Ctrl + K to focus search
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault()
      this.inputTarget.focus()
      this.inputTarget.select()
    }
  }

  search(event) {
    const query = this.inputTarget.value.trim()

    // Clear previous timeout
    clearTimeout(this.searchTimeout)

    // Hide results if query is too short
    if (query.length < 3) {
      this.hideResults()
      return
    }

    // Show loading spinner
    this.spinnerTarget.classList.remove('hidden')

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const response = await fetch(`/tickets_autocomplete.json?q=${encodeURIComponent(query)}`)
      const data = await response.json()

      this.currentResults = data
      this.renderResults(data)
      this.spinnerTarget.classList.add('hidden')
    } catch (error) {
      console.error('Search error:', error)
      this.spinnerTarget.classList.add('hidden')
    }
  }

  renderResults(tickets) {
    if (tickets.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
          <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="mt-2 text-sm font-medium">No tickets found</p>
          <p class="mt-1 text-xs">Try a different search term</p>
        </div>
      `
      this.resultsTarget.classList.remove('hidden')
      return
    }

    const html = tickets.map((ticket, index) => `
      <button
        type="button"
        data-action="click->global-search#selectTicket mouseenter->global-search#hoverResult"
        data-ticket-id="${ticket.id}"
        data-index="${index}"
        class="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700 group"
      >
        <div class="flex items-center gap-3">
          <div class="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
            <i class="fas fa-ticket-alt text-indigo-600 dark:text-indigo-400 text-sm"></i>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-semibold text-indigo-600 dark:text-indigo-400 text-sm">${ticket.uid}</span>
              <span class="text-xs text-gray-500 dark:text-gray-400">•</span>
              <span class="text-sm text-gray-700 dark:text-gray-300 truncate">${this.escapeHtml(ticket.summary)}</span>
            </div>
          </div>
          <div class="flex-shrink-0">
            <i class="fas fa-arrow-right text-gray-400 dark:text-gray-600 text-xs opacity-0 group-hover:opacity-100 transition-opacity"></i>
          </div>
        </div>
      </button>
    `).join('')

    this.resultsTarget.innerHTML = html
    this.resultsTarget.classList.remove('hidden')
    this.selectedIndex = -1
  }

  selectTicket(event) {
    const ticketId = event.currentTarget.dataset.ticketId
    this.startTimer(ticketId)
  }

  async startTimer(ticketId) {
    const authenticity_token = document.querySelector('meta[name="csrf-token"]').content

    try {
      const response = await fetch(`/tickets/${ticketId}/intervals?event=start`, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ authenticity_token })
      })

      const html = await response.text()
      Turbo.renderStreamMessage(html)

      // Clear search
      this.inputTarget.value = ''
      this.hideResults()
      this.inputTarget.blur()
    } catch (error) {
      console.error('Error starting timer:', error)
    }
  }

  handleKeydown(event) {
    const resultsVisible = !this.resultsTarget.classList.contains('hidden')

    if (!resultsVisible) return

    switch(event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectNext()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.selectPrevious()
        break
      case 'Enter':
        event.preventDefault()
        if (this.selectedIndex >= 0) {
          const selectedButton = this.resultsTarget.querySelector(`[data-index="${this.selectedIndex}"]`)
          if (selectedButton) {
            selectedButton.click()
          }
        }
        break
      case 'Escape':
        event.preventDefault()
        this.hideResults()
        this.inputTarget.blur()
        break
    }
  }

  selectNext() {
    const buttons = this.resultsTarget.querySelectorAll('button')
    if (buttons.length === 0) return

    this.selectedIndex = Math.min(this.selectedIndex + 1, buttons.length - 1)
    this.updateSelection(buttons)
  }

  selectPrevious() {
    const buttons = this.resultsTarget.querySelectorAll('button')
    if (buttons.length === 0) return

    this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
    this.updateSelection(buttons)
  }

  updateSelection(buttons) {
    buttons.forEach((button, index) => {
      if (index === this.selectedIndex) {
        button.classList.add('bg-gray-50', 'dark:bg-gray-700')
        button.scrollIntoView({ block: 'nearest' })
      } else {
        button.classList.remove('bg-gray-50', 'dark:bg-gray-700')
      }
    })
  }

  hoverResult(event) {
    this.selectedIndex = parseInt(event.currentTarget.dataset.index)
  }

  showResults() {
    if (this.inputTarget.value.trim().length >= 3 && this.currentResults.length > 0) {
      this.resultsTarget.classList.remove('hidden')
    }
  }

  hideResults() {
    setTimeout(() => {
      this.resultsTarget.classList.add('hidden')
      this.selectedIndex = -1
    }, 200)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
